import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { SITE } from "../config";
import { getUtm } from "../utm";
import { trackEvent } from "../analytics";

const CONFIG_OPTIONS = ["1 BHK", "2 BHK", "3 BHK", "Not Sure"];

const waMessage = (form) =>
    `Hi, I am interested in Atlanta Enclave Shilphata. Name: ${form.name}, Mobile: ${form.mobile}${
        form.email ? `, Email: ${form.email}` : ""
    }, Configuration: ${form.configuration}. Please share the latest price, floor plans and availability.`;

const waLink = (form) => `${SITE.whatsappRaw}?text=${encodeURIComponent(waMessage(form))}`;

export const LeadForm = ({ source = "website", defaultConfig = "", idPrefix = "lead", heading }) => {
    const [form, setForm] = useState({
        name: "",
        mobile: "",
        email: "",
        configuration: defaultConfig || "Not Sure",
        company: "",
    });
    const [startedAt] = useState(() => Date.now());
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        const waWindow = window.open("", "_blank");
        try {
            const res = await fetch("/api/leads.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...form,
                    source,
                    form_started: startedAt,
                    ...getUtm(),
                    current_page: window.location.pathname,
                }),
            });
            const data = await res.json().catch(() => ({ success: false }));
            if (data.success) {
                setStatus("done");
                trackEvent("form_submit", { source, configuration: form.configuration });
                trackEvent("enquiry", { source });
                if (waWindow) waWindow.location.href = waLink(form);
            } else {
                if (waWindow) waWindow.close();
                setStatus("error");
                setError(data.error || "Something went wrong. Please try again.");
            }
        } catch (err) {
            if (waWindow) waWindow.close();
            setStatus("error");
            setError("Could not save right now — please send your enquiry on WhatsApp instead.");
        }
    };

    if (status === "done") {
        return (
            <div className="card-line p-6 text-center" data-testid={`${idPrefix}-success-message`}>
                <p className="font-display text-2xl font-semibold text-[#23231f]">Thank you, {form.name.split(" ")[0]}.</p>
                <p className="mt-2 text-sm text-[#55534a]">
                    Your enquiry has been saved. Our team will call you shortly with the latest price, floor plans and
                    availability for Atlanta Enclave Shilphata.
                </p>
                <a href={waLink(form)} target="_blank" rel="noopener noreferrer" className="btn btn-wa mt-5" data-testid={`${idPrefix}-success-whatsapp-button`}>
                    <MessageCircle size={16} /> Continue on WhatsApp
                </a>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="card-line p-6" data-testid={`${idPrefix}-form`}>
            {heading && <p className="font-display mb-5 text-xl font-semibold text-[#23231f]">{heading}</p>}
            <div className="grid gap-4">
                <div>
                    <label htmlFor={`${idPrefix}-name`} className="label-x">Name *</label>
                    <input id={`${idPrefix}-name`} className="input-x" value={form.name} onChange={set("name")} required autoComplete="name" data-testid={`${idPrefix}-name-input`} />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}-mobile`} className="label-x">Mobile *</label>
                    <input id={`${idPrefix}-mobile`} className="input-x" type="tel" inputMode="tel" value={form.mobile} onChange={set("mobile")} required autoComplete="tel" placeholder="10-digit mobile number" data-testid={`${idPrefix}-mobile-input`} />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}-email`} className="label-x">Email</label>
                    <input id={`${idPrefix}-email`} className="input-x" type="email" value={form.email} onChange={set("email")} autoComplete="email" data-testid={`${idPrefix}-email-input`} />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}-configuration`} className="label-x">Configuration</label>
                    <select id={`${idPrefix}-configuration`} className="input-x" value={form.configuration} onChange={set("configuration")} data-testid={`${idPrefix}-configuration-select`}>
                        {CONFIG_OPTIONS.map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                </div>
                <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={set("company")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                    data-testid={`${idPrefix}-company-honeypot`}
                />
            </div>
            {status === "error" && (
                <div className="mt-3" role="alert">
                    <p className="text-sm font-semibold text-red-700" data-testid={`${idPrefix}-error`}>{error}</p>
                    <a href={waLink(form)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-[#178a45] underline underline-offset-2" data-testid={`${idPrefix}-error-whatsapp-link`}>
                        <MessageCircle size={15} /> Send enquiry on WhatsApp instead
                    </a>
                </div>
            )}
            <button type="submit" className="btn btn-gold mt-5 w-full" disabled={status === "sending"} data-testid={`${idPrefix}-submit-button`}>
                {status === "sending" ? "Sending…" : "Get Project Details"}
            </button>
            <p className="mt-3 text-[11px] leading-relaxed text-[#8a877a]">
                By submitting this form, you authorize us to contact you regarding Atlanta Enclave Shilphata.
            </p>
        </form>
    );
};
